import {useState} from "react";
import {Button, Carousel, Modal} from "react-bootstrap";
import {ProductImageViewModel} from "../../api/models";
import {format, products} from "../../i18n";
import {sizeAttributes} from "../../components/imageSize";

// The product page's images. The carousel's own controls sit on the image, where they're easy to miss, so its
// previous and next buttons and thumbnails are underneath instead, below the image's caption (its alt text). Clicking an
// image shows it full size. It opens on image start (the page may already show the first one).
export function ProductGallery({images, start = 0}: { images: ProductImageViewModel[], start?: number }) {
    const [index, setIndex] = useState(() => Math.min(start, Math.max(images.length - 1, 0)));
    const [zoomed, setZoomed] = useState(false);
    const count = images.length;
    const current = images[Math.min(index, count - 1)];
    const step = (by: number) => setIndex(i => (i + by + count) % count);

    if (!current) {
        return null;
    }

    const imageButton = (image: ProductImageViewModel) => (
        <button type="button" className="product-gallery-image" onClick={() => setZoomed(true)}
                aria-label={image.altText ? products.gallery.viewFullSize : products.gallery.viewProductImageFullSize}
                aria-describedby={image.altText ? 'product-gallery-caption' : undefined}>
            <img src={image.url} alt={image.altText} className="d-block w-100 h-auto rounded" {...sizeAttributes(image)} loading="lazy"/>
        </button>
    );

    // Live, so screen readers say which image the previous and next buttons moved to.
    const caption = (
        <p id="product-gallery-caption" className="text-body-secondary text-center small mt-2 mb-0" aria-live="polite">
            {current.altText}
        </p>
    );

    return (
        <div className="mb-4">
            {count > 1 ? (
                <>
                    <Carousel activeIndex={index} onSelect={setIndex} controls={false} indicators={false} interval={null}>
                        {images.map(image => <Carousel.Item key={image.id}>{imageButton(image)}</Carousel.Item>)}
                    </Carousel>
                    {caption}
                    <div className="d-flex align-items-center gap-2 mt-2">
                        <Button variant="outline-secondary" aria-label={products.gallery.previous} onClick={() => step(-1)}>
                            <i className="bi bi-chevron-left" aria-hidden="true"></i>
                        </Button>
                        <div className="d-flex gap-2 flex-grow-1 overflow-x-auto py-1">
                            {images.map((image, i) => (
                                <button key={image.id} type="button" onClick={() => setIndex(i)}
                                        className={`product-gallery-thumb rounded${i === index ? ' active' : ''}`}
                                        aria-label={format(products.gallery.thumbnail, { number: i + 1, count })} aria-current={i === index || undefined}>
                                    <img src={image.url} alt="" className="rounded"/>
                                </button>
                            ))}
                        </div>
                        <Button variant="outline-secondary" aria-label={products.gallery.next} onClick={() => step(1)}>
                            <i className="bi bi-chevron-right" aria-hidden="true"></i>
                        </Button>
                    </div>
                </>
            ) : (
                <>
                    {imageButton(current)}
                    {caption}
                </>
            )}

            <Modal show={zoomed} onHide={() => setZoomed(false)} fullscreen aria-labelledby="product-gallery-title">
                <Modal.Header closeButton>
                    <Modal.Title id="product-gallery-title" className="fs-6">
                        {current.altText || products.gallery.productImage}{count > 1 && <span className="text-body-secondary ms-2">{format(products.gallery.position, { number: index + 1, count })}</span>}
                    </Modal.Title>
                </Modal.Header>
                <Modal.Body className="d-flex align-items-center justify-content-center">
                    <img src={current.url} alt={current.altText} className="product-gallery-full"/>
                </Modal.Body>
                {count > 1 && (
                    <Modal.Footer className="justify-content-center">
                        <Button variant="outline-secondary" onClick={() => step(-1)}>
                            <i className="bi bi-chevron-left me-1" aria-hidden="true"></i>{products.gallery.previousButton}
                        </Button>
                        <Button variant="outline-secondary" onClick={() => step(1)}>
                            {products.gallery.nextButton}<i className="bi bi-chevron-right ms-1" aria-hidden="true"></i>
                        </Button>
                    </Modal.Footer>
                )}
            </Modal>
        </div>
    );
}
